import React, { Component } from "react";
import { map, always, equals } from "ramda";
import { Maybe } from "true-myth";

import {
  DROPDOWN_SIZE,
  IOption,
  StyledDropdown,
  StyledError,
  StyledDropdownWrapper,
} from "components/shared/dropdown";
import { DROPDOWN_LABEL_POSITION } from "components/shared/labeled_dropdown";
import { noopWhenDisabled } from "helpers/general";
import Sorter from "./sorter";
import EmployeeOption from "./option";
import { sortType } from "interfaces/sort_type";

function defaultKeyFormatter<T>(option: Maybe<IOption<T>>): string {
  return option.map((opt) => `${opt.label}-${opt.id}`).unwrapOr("placeholder");
}

class EmployeeDropdown<T> extends Component<
  IEmployeeDropdownProps<T>,
  IEmployeeDropdownState<T>
> {
  constructor(props: IEmployeeDropdownProps<T>) {
    super(props);

    this.selectedOption = null;
    this.toggleOptions = this.toggleOptions.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.scrollToCurrentOption = this.scrollToCurrentOption.bind(this);

    this.state = {
      expanded: false,
      currentOption: Maybe.of<IOption<T>>(props.currentOption),
    };

    this.selectedOptionRef = (element: HTMLLIElement) => {
      this.selectedOption = element;
    };
    this.node = React.createRef();
    this.dropdownParent = React.createRef();
  }

  private node: React.RefObject<HTMLDivElement>;
  private dropdownParent: React.RefObject<HTMLUListElement>;
  private selectedOption: HTMLLIElement | null;
  private selectedOptionRef: (element: HTMLLIElement) => void;

  public componentDidMount() {
    document.addEventListener("click", this.handleClickOutside, true);
  }

  public componentWillUnmount() {
    document.removeEventListener("click", this.handleClickOutside, true);
  }

  private toggleOptions() {
    const expanded = !this.state.expanded;
    this.setState({ expanded });
  }

  private handleOptionClick(option: Maybe<IOption<T>>) {
    this.updateCurrentOption(option);
    this.props.onOptionClick(option);
  }

  private updateCurrentOption(option: Maybe<IOption<T>>) {
    this.setState({
      expanded: false,
      currentOption: option,
    });
  }

  private scrollToCurrentOption() {
    const { selectedOption, dropdownParent } = this;
    if (selectedOption && dropdownParent.current) {
      dropdownParent.current.scrollTop = selectedOption.offsetTop;
    }
  }

  public componentDidUpdate(prevProps: IEmployeeDropdownProps<T>) {
    const { currentOption } = this.props;
    if (currentOption !== prevProps.currentOption) {
      this.updateCurrentOption(Maybe.of(currentOption));
    }
    this.scrollToCurrentOption();
  }

  public handleClickOutside(event: Event) {
    const { target } = event;

    if (
      target instanceof Element &&
      this.node.current &&
      !this.node.current.contains(target)
    ) {
      this.setState({ expanded: false });
    }
  }

  public render() {
    const {
      disabled = false,
      dropdownName,
      errorMessage,
      keyFormatter,
      options,
      size = DROPDOWN_SIZE.LARGE,
      labelPosition = DROPDOWN_LABEL_POSITION.LEFT,
      sortByName,
      sortStatus,
    } = this.props;
    const { expanded, currentOption } = this.state;
    let renderedOptions;
    if (expanded) {
      renderedOptions = map((opt) => {
        const option = Maybe.of(opt);
        const isCurrentOption: boolean = currentOption
          .map((current) => equals(opt.id, current.id))
          .unwrapOr(false);

        return (
          <EmployeeOption
            createOptionRef={this.selectedOptionRef}
            isExpanded={expanded}
            isSelected={isCurrentOption}
            key={
              keyFormatter ? keyFormatter(option) : defaultKeyFormatter(option)
            }
            option={option}
            {...this.props}
            onOptionClick={this.handleOptionClick}
          />
        );
      }, options);
    } else {
      // Try to find a real option with the same id
      // (this is good in case currentOption goes out of sync with the provided options)

      const selected = currentOption.map((current) => {
        const match = options.find((opt) => equals(opt.id, current.id));
        return match || currentOption.unsafelyUnwrap();
      });

      renderedOptions = (
        <EmployeeOption
          createOptionRef={this.selectedOptionRef}
          isExpanded={expanded}
          isSelected={true}
          key={
            keyFormatter
              ? keyFormatter(currentOption)
              : defaultKeyFormatter(currentOption)
          }
          option={selected}
          onOptionClick={always}
        />
      );
    }

    let renderedErrorMessage;
    if (errorMessage) {
      renderedErrorMessage = (
        <StyledError id={`${dropdownName}-error`} className="error-text">
          {errorMessage}
        </StyledError>
      );
    }

    return (
      <StyledDropdownWrapper
        size={size}
        ref={this.node}
        labelPosition={labelPosition}
      >
        <StyledDropdown
          ref={this.dropdownParent}
          role="listbox"
          aria-describedby={`${dropdownName}-error`}
          disabled={disabled}
          isExpanded={expanded}
          onClick={noopWhenDisabled(this.toggleOptions, disabled)}
          tabIndex={0}
        >
          <Sorter
            isExpanded={expanded}
            sortByName={sortByName}
            status={sortStatus}
          />
          {renderedOptions}
        </StyledDropdown>
        {renderedErrorMessage}
      </StyledDropdownWrapper>
    );
  }
}

export interface IEmployeeDropdownProps<T> {
  currentOption?: IOption<T>;
  disabled?: boolean;
  dropdownName: string; // For aria labelling
  errorMessage?: string;
  keyFormatter?: (option: Maybe<IOption<T>>) => string;
  onOptionClick: (option: Maybe<IOption<T>>) => void;
  options: Array<IOption<T>>;
  size?: DROPDOWN_SIZE;
  labelPosition?: DROPDOWN_LABEL_POSITION;
  sortByName: () => void;
  sortStatus: sortType;
}

export interface IEmployeeDropdownState<T> {
  expanded: boolean;
  currentOption: Maybe<IOption<T>>;
}

export default EmployeeDropdown;
